import React from 'react';
import {
  filterDriverStatementRows,
  getStatementRowCompany,
} from '../utils/driverStatementFilter';

interface StatementTemplatePageProps {
  isFullScreen?: boolean;
  setIsFullScreen?: (v: boolean) => void;
  company?: string;
  dateRange?: string;
  totalAmount?: number;
  rows?: any[];
  pdfMode?: boolean;
  templateType?: string;
  supplier: {
    name: string;
    registrationNumber: string;
    ceo: string;
    address: string;
    phone: string;
    fax: string;
  };
}

// 예시 데이터 (실제 구현시 props/state로 대체)
interface StatementRow {
  date: string;
  vehicleNumber: string;
  route: string;
  cargo: string;
  billingAmount: number;
}
const clientName = '거래처 or 차량번호';
const itemName = '품명이 입력되어야 하는곳';
const amount = 15900000;
const rows: StatementRow[] = [
  // 예시 데이터 (실제 구현시 동적)
  // { date: '2025-05-19', vehicleNumber: '5270', route: 'BICT-삼남-BICT', cargo: '20FT', billingAmount: 290000 },
];

const defaultSupplier = {
  registrationNumber: '',
  name: '',
  ceo: '',
  address: '',
  phone: '',
  fax: ''
};

/** jsPDF 페이지번호(- N -) 영역 */
const PDF_PAGE_NUMBER_CLEARANCE = 48;
const PDF_BASE_ROW_HEIGHT = 32;

/** 1페이지(헤더 포함) / 이후 페이지 목표 행 수 — 픽셀 추정 대신 고정 슬롯으로 분할 */
const STATEMENT_ROWS_FIRST_PAGE = 24;
const STATEMENT_ROWS_PER_PAGE = 33;
/** 마지막 페이지 합계금액 영역 예약(행 슬롯) */
const STATEMENT_FOOTER_ROW_RESERVE = 2;

function statementRowSpan(row: any): number {
  const extraLen = Math.max(
    String(row.route ?? '').length,
    String(row.vehicleNumber ?? '').length,
    String(row.cargo ?? '').length
  );
  if (extraLen <= 22) return 1;
  if (extraLen <= 44) return 2;
  return 3;
}

function estimateStatementRowHeight(row: any): number {
  return PDF_BASE_ROW_HEIGHT + (statementRowSpan(row) - 1) * 14;
}

function firstPageRowLimit(company?: string): number {
  const extraLines = Math.max(0, Math.ceil(String(company ?? '').trim().length / 12) - 1);
  return Math.max(22, STATEMENT_ROWS_FIRST_PAGE - extraLines * 2);
}

function pageRowLimit(pageIndex: number, company?: string): number {
  return pageIndex === 0 ? firstPageRowLimit(company) : STATEMENT_ROWS_PER_PAGE;
}

function pageRowWeight(page: any[]): number {
  return page.reduce((sum, row) => sum + (row._rowSpan ?? 1), 0);
}

/** 고정 슬롯 기반 페이지 분할 — 행 누락 없이 안전하게 처리 */
function paginateStatementRows(rows: any[], company?: string): any[][] {
  if (rows.length === 0) return [];

  const pages: any[][] = [];
  let currentPage: any[] = [];
  let currentWeight = 0;
  let currentIndex = 0;

  for (let i = 0; i < rows.length; i++) {
    const rowSpan = statementRowSpan(rows[i]);
    const limit = pageRowLimit(pages.length, company);

    if (currentPage.length > 0 && currentWeight + rowSpan > limit) {
      pages.push(currentPage);
      currentPage = [];
      currentWeight = 0;
    }

    currentIndex += 1;
    currentPage.push({
      ...rows[i],
      index: currentIndex,
      _rowSpan: rowSpan,
      _rowHeight: estimateStatementRowHeight(rows[i]),
    });
    currentWeight += rowSpan;
  }

  if (currentPage.length > 0) pages.push(currentPage);

  // 마지막 페이지에 합계금액이 들어갈 공간 확보
  while (pages.length > 0) {
    const lastPage = pages[pages.length - 1];
    const footerLimit = pageRowLimit(pages.length - 1, company) - STATEMENT_FOOTER_ROW_RESERVE;
    if (pageRowWeight(lastPage) <= footerLimit || lastPage.length <= 1) break;
    const movedRow = lastPage.pop()!;
    pages.push([movedRow]);
  }

  return pages;
}

const pdfTotalFooterStyle: React.CSSProperties = {
  marginTop: 16,
  pageBreakBefore: 'avoid',
  breakBefore: 'avoid',
  pageBreakInside: 'avoid',
  breakInside: 'avoid',
};

const pdfPageChunkStyle = (pageIndex: number, pageCount: number): React.CSSProperties => ({
  width: '100%',
  pageBreakAfter: pageIndex < pageCount - 1 ? 'always' : 'auto',
  breakAfter: pageIndex < pageCount - 1 ? 'page' : 'auto',
  pageBreakInside: 'auto',
  breakInside: 'auto',
  minHeight: 'auto',
  height: 'auto',
  paddingBottom: pageIndex === pageCount - 1 ? PDF_PAGE_NUMBER_CLEARANCE : 8,
});

const pdfTableStyle = (marginTop: number): React.CSSProperties => ({
  width: '100%',
  borderCollapse: 'collapse',
  marginTop,
  fontSize: 15,
  tableLayout: 'fixed',
  pageBreakInside: 'auto',
  breakInside: 'auto',
});

const StatementTemplatePage: React.FC<StatementTemplatePageProps> = ({ isFullScreen, setIsFullScreen, company, dateRange, totalAmount, rows, pdfMode, templateType, supplier = defaultSupplier }) => {
  // 탭 메뉴 정의 추가
  const [activeTab, setActiveTab] = React.useState<'화주명세서' | '기사명세서'>('화주명세서');

  // pdfMode일 때 templateType에 따라 강제 분기
  const isDriver = pdfMode && templateType === 'C';

  // 기사명세서일 때 차량번호 아래 표시할 사업자 계좌 문구 결정
  const driverAccountLine = React.useMemo(() => {
    if (!(isDriver || activeTab === '기사명세서')) return '';
    const supplierName = (supplier?.name || '').replace(/\s+/g, '');
    const hasCompany = (target: string) =>
      Array.isArray(rows) && rows.some((r) => getStatementRowCompany(r) === target);
    // 조건 1) 공급자: 성우로지스, 거래처상호: 지입료
    if (/성우로지스/.test(supplierName) && hasCompany('지입료')) {
      return '국민 078737-01-001847 ( 성우로지스 )';
    }
    // 조건 2) 공급자: 한국운수, 거래처상호: 화물공제보험
    if (/한국운수/.test(supplierName) && hasCompany('화물공제보험')) {
      return '기업 938-020507-01-014 ( 장원석 )';
    }
    return '';
  }, [isDriver, activeTab, supplier, rows]);

  // 기사명세서 조건부 행 필터링
  const driverRows = React.useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!(isDriver || activeTab === '기사명세서')) return list;
    return filterDriverStatementRows(list, supplier?.name || '');
  }, [isDriver, activeTab, supplier, rows]);

  // 기사명세서 결산금액(필터 반영)
  const driverTotalAmount = React.useMemo(() => {
    return (Array.isArray(driverRows)
      ? driverRows.reduce((sum: number, r: any) => sum + (Number(r.paymentAmount) || 0), 0)
      : 0);
  }, [driverRows]);

  return (
    <div
      style={{
        width: '100%',
        minHeight: pdfMode ? 'auto' : '100vh', // PDF 모드에서는 자동 높이
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        background: pdfMode ? '#fff' : '#f5f7fa', // PDF 모드에서는 흰색 배경
      }}
    >
      <div
        style={{
          width: 794, // A4 세로 고정
          minHeight: pdfMode ? 'auto' : 1123, // PDF 모드에서는 자동 높이
          margin: pdfMode ? '0' : '40px 0', // PDF 모드에서는 여백 제거
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {!pdfMode && (
          <div style={{ display: 'flex', gap: 0, marginBottom: 18 }}>
            <button
              onClick={() => setActiveTab('화주명세서')}
              style={{
                padding: '10px 32px',
                border: 'none',
                borderBottom: activeTab === '화주명세서' ? '3px solid #1976d2' : '1px solid #ddd',
                background: activeTab === '화주명세서' ? '#fff' : '#f5f7fa',
                color: activeTab === '화주명세서' ? '#1976d2' : '#888',
                fontWeight: activeTab === '화주명세서' ? 700 : 500,
                fontSize: 16,
                cursor: 'pointer',
                outline: 'none',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 0,
                transition: 'all 0.15s'
              }}
            >
              화주명세서
            </button>
            <button
              onClick={() => setActiveTab('기사명세서')}
              style={{
                padding: '10px 32px',
                border: 'none',
                borderBottom: activeTab === '기사명세서' ? '3px solid #1976d2' : '1px solid #ddd',
                background: activeTab === '기사명세서' ? '#fff' : '#f5f7fa',
                color: activeTab === '기사명세서' ? '#1976d2' : '#888',
                fontWeight: activeTab === '기사명세서' ? 700 : 500,
                fontSize: 16,
                cursor: 'pointer',
                outline: 'none',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 8,
                transition: 'all 0.15s'
              }}
            >
              기사명세서
            </button>
          </div>
        )}
        <div id="statement-preview"
          style={{
            width: 794,
            minHeight: pdfMode ? 'auto' : 1123, // PDF 모드에서는 자동 높이
          }}
        >
         
          <div style={{ 
            background: '#fff', 
            border: pdfMode ? 'none' : '1px solid #bbb', // PDF 모드에서는 테두리 제거
            borderRadius: pdfMode ? 0 : 8, // PDF 모드에서는 둥근 모서리 제거
            padding: pdfMode ? '20px 40px' : '32px 40px', // PDF 모드에서는 패딩 줄임
            minHeight: pdfMode ? 'auto' : 500, // PDF 모드에서는 자동 높이
            fontFamily: 'Noto Sans KR, Pretendard, Arial, sans-serif', 
            boxSizing: 'border-box', 
            width: '100%', 
            margin: '0 auto',
            ...(pdfMode && {
              pageBreakInside: 'auto',
              breakInside: 'auto',
              display: 'block',
            })
          }}>
            <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: 4, marginBottom: 16 }}>거래명세서</div>
            {/* 상단 3단 테이블 구조 - 엑셀과 완전히 동일하게 */}
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '2px solid #222', margin: '0 auto 8px auto', fontSize: 16, tableLayout: 'fixed' }}>
              <colgroup><col style={{ width: '28%' }} /><col style={{ width: '8%' }} /><col style={{ width: '7%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /></colgroup>
              <tbody>
                <tr>
                  {/* 거래처(좌) */}
                  <td rowSpan={5} style={{ borderRight: '1px solid #222', fontWeight: 600, textAlign: 'center', verticalAlign: 'middle', padding: 0, fontSize: 17 }}>
                    <div style={{ padding: 16, whiteSpace: 'pre-line' }}>
                      {company || '거래처상호 or 차량번호'}
                      {(driverAccountLine) && (
                        <div style={{ marginTop: 6, fontWeight: 700, fontSize: 15 }}>
                          {driverAccountLine}
                        </div>
                      )}
                    </div>
                  </td>
                  {/* 공급자(중앙) */}
                  <td rowSpan={5} style={{ borderRight: '1px solid #222', borderLeft: '1px solid #222', fontWeight: 700, textAlign: 'center', verticalAlign: 'middle', fontSize: 17, padding: 0 }}>
                    공급자
                  </td>
                  {/* 공급자정보(우) - 1행: 등록번호 */}
                  <td style={{ fontWeight: 600, borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '0 2px', whiteSpace: 'nowrap' }}>등록번호</td>
                  <td colSpan={2} style={{ borderBottom: '1px solid #222', padding: '0 4px' }}>{supplier.registrationNumber}</td>
                </tr>
                <tr>
                  {/* 2행: 상호, 대표자 */}
                  <td style={{ fontWeight: 600, borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '0 2px', whiteSpace: 'nowrap' }}>상호</td>
                  <td style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '0 4px' }}>{supplier.name}</td>
                  <td style={{ borderBottom: '1px solid #222', padding: '0 4px' }}>대표자 {supplier.ceo}</td>
                </tr>
                <tr>
                  {/* 3행: 주소 */}
                  <td style={{ fontWeight: 600, borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '0 2px', whiteSpace: 'nowrap' }}>주소</td>
                  <td colSpan={2} style={{ borderBottom: '1px solid #222', padding: '0 4px', fontSize: 14 }}>{supplier.address}</td>
                </tr>
                <tr>
                  {/* 4행: 전화번호, FAX */}
                  <td style={{ fontWeight: 600, borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '0 2px', whiteSpace: 'nowrap' }}>전화번호</td>
                  <td style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '0 4px' }}>{supplier.phone}</td>
                  <td style={{ borderBottom: '1px solid #222', padding: '0 4px' }}>FAX {supplier.fax}</td>
                </tr>
                <tr>
                  {/* 5행: 빈칸(공급자정보란 최소 높이 맞춤용) */}
                  <td colSpan={3} style={{ padding: 0, border: 'none', height: 0 }}></td>
                </tr>
              </tbody>
            </table>
            {/* 표 영역: templateType에 따라 분기 */}
            {(isDriver || activeTab === '기사명세서') ? (
              // 기사명세서 양식
              <>
                <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 600, margin: '18px 0 2px 0', letterSpacing: 1 }}>
                  결산금액 : <span style={{ color: '#1976d2', fontWeight: 700 }}>{driverTotalAmount.toLocaleString()}원</span> ( 부가세 별도 )
                  {dateRange && <span style={{ marginLeft: 8, color: '#222', fontWeight: 400, fontSize: 15 }}> / {dateRange}</span>}
                </div>
                {(() => {
                  if (!driverRows || driverRows.length === 0) {
                    return (
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 15, tableLayout: 'fixed' }}>
                        <colgroup><col style={{ width: '6%' }} /><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /><col style={{ width: '38%' }} /><col style={{ width: '10%' }} /><col style={{ width: '16%' }} /></colgroup>
                        <thead>
                          <tr style={{ background: '#f5f7fa' }}>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>순번</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>날짜</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>차종</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>운행구간</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>미지불금</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>누계</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td colSpan={6} style={{ textAlign: 'center', color: '#aaa', padding: 24 }}>표 데이터가 없습니다.</td></tr>
                        </tbody>
                      </table>
                    );
                  }

                  const pages = paginateStatementRows(driverRows, company);

                  return pages.map((pageData, pageIndex) => (
                    <div
                      key={pageIndex}
                      className="pdf-page"
                      style={pdfPageChunkStyle(pageIndex, pages.length)}
                    >
                      <table style={pdfTableStyle(pageIndex === 0 ? 8 : 0)}>
                        <colgroup>
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '16%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '38%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '16%' }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: '#f5f7fa' }}>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>순번</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>날짜</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>차종</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>운행구간</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>미지불금</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>누계</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageData.map((row, idx) => {
                            const payment = Number(row.paymentAmount) || 0;
                            const rowHeight = row._rowHeight ?? PDF_BASE_ROW_HEIGHT;
                            const 누계 = driverRows.slice(0, row.index).reduce((sum: number, r: any) => sum + (Number(r.paymentAmount) || 0), 0);
                            return (
                              <tr key={idx} style={{
                                pageBreakInside: 'avoid',
                                breakInside: 'avoid',
                                minHeight: rowHeight,
                              }}>
                                <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.index}</td>
                                <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.date}</td>
                                <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.cargo}</td>
                                <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.route}</td>
                                <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{payment.toLocaleString()}</td>
                                <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{누계.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {pageIndex === pages.length - 1 && (
                        <div style={{
                          ...pdfTotalFooterStyle,
                          textAlign: 'right',
                          fontWeight: 700,
                          fontSize: 18,
                          marginRight: 12
                        }}>
                          합계금액 : {(driverRows && driverRows.length > 0 ? driverRows.reduce((sum: number, r: any) => sum + (Number(r.paymentAmount) || 0), 0).toLocaleString() : 0)}
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </>
            ) : (
              // 화주명세서 양식
              <>
                <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 600, margin: '18px 0 2px 0', letterSpacing: 1 }}>
                  청구운송료 : <span style={{ color: '#1976d2', fontWeight: 700 }}>{(totalAmount ?? 0).toLocaleString()}원</span> ( 부가세 별도 )
                  {dateRange && <span style={{ marginLeft: 8, color: '#222', fontWeight: 400, fontSize: 15 }}> / {dateRange}</span>}
                </div>
                <style>
                  {`
                    @page {
                      margin: 20mm 15mm 30mm 15mm; /* 하단 여백을 30mm로 설정 */
                      size: A4;
                    }
                    @media print {
                      table {
                        page-break-inside: auto;
                      }
                      tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                      }
                      thead {
                        display: table-header-group;
                      }
                      tfoot {
                        display: table-footer-group;
                      }
                    }
                  `}
                </style>
                {(() => {
                  if (!rows || rows.length === 0) {
                    return (
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 15, tableLayout: 'fixed' }}>
                        <colgroup><col style={{ width: '6%' }} /><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /><col style={{ width: '38%' }} /><col style={{ width: '10%' }} /><col style={{ width: '16%' }} /></colgroup>
                        <thead>
                          <tr style={{ background: '#f5f7fa' }}>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>순번</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>날짜</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>차량</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>운행구간</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>중량</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>금액</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td colSpan={6} style={{ textAlign: 'center', color: '#aaa', padding: 24 }}>표 데이터가 없습니다.</td></tr>
                        </tbody>
                      </table>
                    );
                  }

                  // company명 기준 1페이지 행 수 조정 (firstPageTopHeight 제거)
                  const pages = paginateStatementRows(rows, company);

                  return pages.map((pageData, pageIndex) => (
                    <div
                      key={pageIndex}
                      className="pdf-page"
                      style={pdfPageChunkStyle(pageIndex, pages.length)}
                    >
                      <table style={pdfTableStyle(pageIndex === 0 ? 8 : 0)}>
                        <colgroup><col style={{ width: '6%' }} /><col style={{ width: '16%' }} /><col style={{ width: '14%' }} /><col style={{ width: '38%' }} /><col style={{ width: '10%' }} /><col style={{ width: '16%' }} /></colgroup>
                        <thead>
                          <tr style={{ background: '#f5f7fa' }}>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>순번</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>날짜</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>차량</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>운행구간</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>중량</th>
                            <th style={{ border: '1px solid #bbb', padding: 6 }}>금액</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageData.map((row, idx) => {
                            const rowHeight = row._rowHeight ?? PDF_BASE_ROW_HEIGHT;
                            return (
                            <tr key={idx} style={{
                              pageBreakInside: 'avoid',
                              breakInside: 'avoid',
                              minHeight: rowHeight,
                            }}>
                              <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.index}</td>
                              <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.date}</td>
                              <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.vehicleNumber}</td>
                              <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.route}</td>
                              <td style={{ border: '1px solid #ddd', textAlign: 'center', padding: '4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{row.cargo}</td>
                              <td style={{ border: '1px solid #ddd', textAlign: 'right', padding: '4px 8px 4px 2px', fontFamily: 'Noto Sans KR, sans-serif' }}>{(Number(row.billingAmount) || 0).toLocaleString()}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {pageIndex === pages.length - 1 && (
                        <div style={pdfTotalFooterStyle}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 0, fontSize: 16 }}>
                            <tbody>
                              <tr>
                                <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700, fontSize: 16, padding: '12px 12px 0 0', border: 'none', background: 'transparent' }}>
                                  합 계 금 액
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 16, padding: '12px 8px 0 0', border: 'none', background: 'transparent' }}>
                                  {(rows && rows.length > 0
                                    ? rows.reduce((sum: number, r: any) => sum + (Number(r.billingAmount) || 0), 0).toLocaleString()
                                    : 0)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatementTemplatePage; 